import { protectedApi } from "@/store/api/api";
import React, { useEffect, useMemo, useState } from "react";

// reuse helpers
const fmtMoney = (n) => {
  const currency = import.meta.env.VITE_REACT_CURRENCY;
  `${currency} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
const asDate = (ts) => {
  if (!ts) return null;
  if (typeof ts?.toDate === "function") return ts.toDate();
  if (typeof ts === "number") return new Date(ts);
  return new Date(ts);
};
const fmtDateTime = (ts) => {
  const d = asDate(ts);
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : "—";
};

const PayoutsTable = ({ rows }) => {
  if (!rows?.length) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 rounded p-6 text-slate-600 dark:text-slate-400">
        No payouts found.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-white dark:bg-slate-900 rounded border">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
          <tr>
            <th className="text-left px-4 py-3">Organiser</th>
            <th className="text-left px-4 py-3">Period</th>
            <th className="text-left px-4 py-3">Gross</th>
            <th className="text-left px-4 py-3">Platform 15%</th>
            <th className="text-left px-4 py-3">Your 85%</th>
            <th className="text-left px-4 py-3">Final Payout</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Reference</th>
            <th className="text-left px-4 py-3">Created</th>
            <th className="text-left px-4 py-3">Paid</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const id = p.id || p.payoutId;
            const period =
              p.periodStart || p.periodEnd
                ? `${fmtDateTime(p.periodStart)} → ${fmtDateTime(p.periodEnd)}`
                : "—";

            const gross = p.cycleGross ?? p.gross ?? p.amount ?? 0;
            const fee15 =
              p.fee15 ??
              Math.round((gross * 0.15 + Number.EPSILON) * 100) / 100;
            const share85 =
              p.share85 ??
              Math.round((gross * 0.85 + Number.EPSILON) * 100) / 100;
            const finalPayout = p.finalPayout ?? share85;

            const status = String(p.status || "—").toLowerCase();

            return (
              <tr
                key={id}
                className="border-t border-slate-100 dark:border-slate-800"
              >
                <td className="px-4 py-3">{p.organiserId || "—"}</td>
                <td className="px-4 py-3">{period}</td>
                <td className="px-4 py-3">{fmtMoney(gross)}</td>
                <td className="px-4 py-3">{fmtMoney(fee15)}</td>
                <td className="px-4 py-3">{fmtMoney(share85)}</td>
                <td className="px-4 py-3 font-medium">
                  {fmtMoney(finalPayout)}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded px-2 py-0.5 text-xs ${
                      status === "paid"
                        ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300"
                        : status === "pending"
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
                        : "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                    }`}
                  >
                    {String(p.status || "—").toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3">{p.reference || "—"}</td>
                <td className="px-4 py-3">{fmtDateTime(p.createdAt)}</td>
                <td className="px-4 py-3">{fmtDateTime(p.paidAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AllPayouts = () => {
  const [data, setData] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const resp = await protectedApi.get("/api/payouts", {
          params: { limit },
        });
        if (!mounted) return;
        const items = resp?.data?.payouts || [];
        setData(items);
      } catch (e) {
        if (!mounted) return;
        setErr(
          e?.response?.data?.error || e?.message || "Failed to load payouts"
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [limit]);

  const filtered = useMemo(() => {
    let rows = data.slice();
    if (status !== "all") {
      rows = rows.filter((r) => (r.status || "").toLowerCase() === status);
    }
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      rows = rows.filter((r) =>
        [r.reference, r.notes, r.organiserId]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(n))
      );
    }
    rows.sort((a, b) => {
      const aT = asDate(a.createdAt)?.getTime() || 0;
      const bT = asDate(b.createdAt)?.getTime() || 0;
      return bT - aT;
    });
    return rows;
  }, [data, status, q]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">All Payouts</h1>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reference/notes/organiser"
            className="form-input rounded border px-3 py-2 text-sm"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="form-select rounded border px-3 py-2 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={String(limit)}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="form-select rounded border px-3 py-2 text-sm"
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n} rows
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="bg-slate-50 dark:bg-slate-900 rounded p-6">
          <div className="animate-pulse h-5 w-40 bg-slate-200 dark:bg-slate-800 rounded mb-3" />
          <div className="animate-pulse h-5 w-3/5 bg-slate-200 dark:bg-slate-800 rounded mb-3" />
          <div className="animate-pulse h-5 w-2/5 bg-slate-200 dark:bg-slate-800 rounded" />
        </div>
      )}

      {err && !loading && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded p-4">
          {err}
        </div>
      )}

      {!loading && !err && <PayoutsTable rows={filtered} />}
    </div>
  );
};

export default AllPayouts;
